import React, { useMemo, useState, useEffect, useCallback } from 'react';

import debounce from 'lodash/debounce';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle } from '@fortawesome/pro-light-svg-icons/faQuestionCircle';
import { faPlus } from '@fortawesome/pro-light-svg-icons/faPlus';

import Form from 'epn-ui/umd/dist/components/Form';
import InputText from 'epn-ui/umd/dist/components/InputText';
import Select from 'epn-ui/umd/dist/components/Select';
import Button from 'epn-ui/umd/dist/components/Button';
import Skeleton from 'epn-ui/umd/dist/components/Skeleton';
import TooltipPro from 'epn-ui/umd/dist/components/TooltipPro';
import Checkbox, { CheckboxChangeEvent } from 'epn-ui/umd/dist/components/CheckboxInput';
import notification from 'epn-ui/umd/dist/components/Notification';

import BaseDragAndDrop from '@components/base/BaseDragAndDropAttach';
import BaseSemanticLink from '@base/BaseSemanticLink';

import api from '@api';

import handleErrorResponse from '@api/helpers/handleErrorResponse';
import { creativeErrors } from '@api/errorConfigs';

import useI18n from '@i18n';

import routes from '@config/routes.json';
import { CREATE_CREATIVE_ERRORS, INPUT_NAMES_BY_CREATIVE_ERROR } from '@api/errors';
import { companyName } from '@config/globalDomain';
import { WITHOUT_SPACE_START_END } from '@utils/regex';
import { ORD_FORM_OPTIONS, ORD_FORM_TYPES } from '../config';

// TYPES
import { IForm, IFormMarkingProps } from '../interfaces';

const FormMarking: React.FC<IFormMarkingProps> = ({
    offersIsLoading,
    placementsIsLoading,
    offers,
    onChangeOffer,
    placements,
    reductionList,
    reductionListLoading,
    onChangeReduction,
    reductionType,
    reductionsNeed,
    setReductionsNeed,
    onSubmitForm,
    formError,
    offerId,
    isLoading,
}) => {
    const [subIdInputs, setSubIdInputs] = useState<string[]>([]);
    const [searchOffer, setSearchOffer] = useState<boolean>(false);
    const [fileError, setFileError] = useState<string | null>(null);
    const { t } = useI18n();
    const [form] = Form.useForm<IForm>();
    const ordForm = Form.useWatch('ordForm', form);
    const link = Form.useWatch('link', form);

    // Get Offer by link
    const getOfferIdByLink = useCallback(async (url: string): Promise<string | null> => {
        try {
            const { data } = await api.offer.getOfferByLink({ params: { link: url } });
            // если ссылка принадлежит офферу, то берем id из первого подходящего
            const { id } = data?.[0];
            return id;
        } catch (error) {
            return null;
        }
    }, []);

    // Проверка ссылки на соответствие офферу
    const validateLink = debounce(async (inputLink: string) => {
        try {
            if (new URL(inputLink)) {
                setSearchOffer(true);
                const id = await getOfferIdByLink(inputLink);
                const hasOffer = offers.some(offer => Number(offer.id) === Number(id));
                if (!id || !hasOffer) {
                    form.setFields([
                        {
                            name: 'link',
                            errors: [t('The link does not relate to the offer')],
                        },
                    ]);
                    return;
                }
                form.setFields([{ name: 'link', errors: [] }]);
                form.setFields([{ name: 'offerId', value: id }]);
                onChangeOffer(id);
            }
        } catch (error) {
            form.setFields([
                {
                    name: 'link',
                    errors: [t('Incorrect link for the offer')],
                },
            ]);
        } finally {
            setSearchOffer(false);
        }
    }, 700);

    // Ищу оффер под ссылку
    useEffect(() => {
        if (link && link.length > 0) {
            validateLink(link);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [link]);

    const validateOffer = useCallback(async () => {
        if (!link) return;
        try {
            const id = await getOfferIdByLink(link);
            if (Number(id) !== Number(offerId)) {
                form.setFields([
                    {
                        name: 'link',
                        errors: [t('The link does not relate to the offer')],
                    },
                ]);
                return;
            }
            form.setFields([{ name: 'link', errors: [] }]);
        } catch (error) {
            form.setFields([
                {
                    name: 'link',
                    errors: [t('Incorrect link for the offer')],
                },
            ]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getOfferIdByLink, link, offerId]);

    const handleFileError = useCallback(() => {
        if (Array.isArray(formError)) {
            formError.forEach(item => {
                if (item.error === 'ordRim') {
                    setFileError(CREATE_CREATIVE_ERRORS[item.error] || item.description);
                    return;
                }
                // TODO Нужно чтоб бекенд возвращал поле ошибки
                if (INPUT_NAMES_BY_CREATIVE_ERROR[item.error]) {
                    form.setFields([
                        {
                            name: INPUT_NAMES_BY_CREATIVE_ERROR[item.error],
                            errors: [
                                ...form.getFieldError(INPUT_NAMES_BY_CREATIVE_ERROR[item.error]),
                                t(CREATE_CREATIVE_ERRORS[item.error]),
                            ],
                        },
                    ]);
                }
                notification.error({
                    message: `${item.error} ${t(
                        CREATE_CREATIVE_ERRORS[item.error] || item.error_description,
                    )}`,
                });
            });
        } else {
            form.resetFields();
            setFileError(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formError]);

    // Проверяю ссылку под оффер
    useEffect(() => {
        if (offerId) {
            debounce(async () => {
                setSearchOffer(true);
                await validateOffer();
                setSearchOffer(false);
            }, 700)();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offerId]);

    useEffect(() => {
        handleFileError();
    }, [handleFileError]);

    const ordFormOptions = ORD_FORM_OPTIONS(t);

    const preparedOffers = useMemo(
        () =>
            offers.map(item => ({
                label: item.name,
                value: item.id,
            })),
        [offers],
    );

    const preparedPlacements = useMemo(
        () =>
            placements.map(item => ({
                label: item.url,
                value: item.id,
            })),
        [placements],
    );

    // Добавляем инпуты для сабов
    const onAddSubId = index => {
        if (subIdInputs.length >= 4) return;
        setSubIdInputs(prev => [...prev, `subid-${index}`]);
    };

    const onAddFile = useCallback(
        newFiles => {
            if (!newFiles.length) {
                form.setFieldsValue({ ordRim: [] });
                return;
            }
            form.setFieldsValue({ ordRim: newFiles[0].originFileObj });
        },
        [form],
    );

    const onChangeReductionType = (value: string) => {
        onChangeReduction(value);
    };

    const onCheckReductionsType = (e: CheckboxChangeEvent) => {
        setReductionsNeed(e.target.checked);
    };

    const onSubmit = async formData => {
        // Проставляю дефолтные поля
        const preparedForm = {
            ...formData,
            type: 'deeplink',
            offerId: formData.offerId || preparedOffers[0].value,
            placementId: formData.placementId || preparedPlacements[0].value,
            ordForm: formData.ordForm || ordFormOptions[0]?.value,
        };
        // Проверяю добавлены ли файлы
        if (preparedForm.ordForm !== ORD_FORM_TYPES.text) {
            if (!formData.ordRim) {
                form.setFields([{ name: 'ordRim', errors: [t('Add an image')] }]);
                return;
            }
        }

        const result = await onSubmitForm(preparedForm);

        if (!result?.result) {
            const { formErrors, generalErrors } = await handleErrorResponse(
                result,
                creativeErrors,
                t,
                'markingForm',
            );
            if (formErrors.length > 0) {
                form.setFields(formErrors);
            }
            if (generalErrors.length > 0) {
                generalErrors.forEach(item => notification.error({ message: item }));
            }
            return;
        }

        form.resetFields();
        setSubIdInputs([]);
    };

    // Ошибка, если нет площадок для данного оффера
    const noPlacementsError =
        offerId && !placementsIsLoading && Array.isArray(placements) && !placements.length;

    // Обязательное поле для этих типов
    const ordTextIsRequires =
        ordForm === ORD_FORM_TYPES.text || ordForm === ORD_FORM_TYPES.textGraphic;

    const ordRimIsRequired =
        ordForm === ORD_FORM_TYPES.banner || ordForm === ORD_FORM_TYPES.textGraphic;

    return (
        <Form
            name="form_marking"
            onFinish={onSubmit}
            form={form}
            initialValues={{ ordForm: ordFormOptions[0]?.value }}
        >
            <Form.Item
                label={t('Insert your link here')}
                name="link"
                required
                rules={[
                    {
                        required: ordTextIsRequires,
                        whitespace: true,
                        message: t('Required field'),
                    },
                ]}
            >
                <InputText placeholder={t('Example: https://aliexpress.ru')} />
            </Form.Item>
            <Form.Item
                label={
                    <>
                        {t('Name of creative')}&nbsp;
                        <TooltipPro
                            title={t(
                                'Add the name of your creative for convenience. It is used only in ePN',
                                { companyName },
                            )}
                            //  @ts-expect-error TODO Нужно поправить пропсы компонента
                            overlayInnerStyle={{ minWidth: '305px' }}
                        >
                            <FontAwesomeIcon
                                className="cursor-pointer hover:text-blueGray-800"
                                icon={faQuestionCircle}
                            />
                        </TooltipPro>
                    </>
                }
                name="description"
                rules={[
                    {
                        max: 100,
                        message: t('Maximum length of characters', { max: 100 }),
                    },
                ]}
            >
                <InputText placeholder={t('for example: jewelry in footer')} />
            </Form.Item>
            <Form.Item label={t('Choose offer')} name="offerId" required>
                {offersIsLoading ? (
                    <Skeleton.Input active className="!w-full" />
                ) : (
                    <Select
                        options={preparedOffers}
                        defaultValue={preparedOffers[0]?.value}
                        onChange={onChangeOffer}
                        loading={searchOffer}
                        filterOption={(input, option) =>
                            (option?.label.toString().toLowerCase() ?? '').includes(
                                input.toLowerCase(),
                            )
                        }
                    />
                )}
            </Form.Item>
            <Form.Item
                label={t('Choose a traffic source')}
                name="placementId"
                help={
                    <span className="text-p3 text-blueGray-500">
                        {t('Creative can only be used for one offer and placement')}
                    </span>
                }
                required
            >
                {placementsIsLoading ? (
                    <Skeleton.Input active className="!w-full" />
                ) : (
                    <Select
                        options={preparedPlacements}
                        defaultValue={preparedPlacements[0]?.value}
                    />
                )}
            </Form.Item>
            {noPlacementsError && (
                <div className="mt-2 mb-6 text-p2">
                    <span className="text-red-400">
                        {t('Please add your traffic source for this offer')}.
                    </span>{' '}
                    <BaseSemanticLink
                        className="underline text-blueGray-800"
                        to={routes.placements.add}
                    >
                        {t('Add traffic source')}
                    </BaseSemanticLink>
                </div>
            )}

            <div className="relative">
                <Form.Item label={t('Insert subid if needed')} name="sub">
                    <InputText placeholder="sub" />
                </Form.Item>
                <button
                    className="absolute right-0 flex items-center justify-center w-10 h-10 top-5"
                    id="AffiliateInputsAddSubIdButton"
                    type="button"
                    onClick={onAddSubId}
                >
                    <FontAwesomeIcon className="block text-blueGray-800 text-sub" icon={faPlus} />
                </button>
            </div>
            {Object.keys(subIdInputs).map((_item, index) => (
                <Form.Item name={`sub${index + 2}`} key={`sub${index + 2}`}>
                    <InputText placeholder={`sub${index + 2}`} />
                </Form.Item>
            ))}

            <Form.Item
                label={t('Select creative type')}
                name="ordForm"
                required
                help={
                    <span className="text-p3 text-blueGray-500">
                        {t(
                            'Example: for a post with a text and an image, select Text and Graphics',
                        )}
                    </span>
                }
                className="mb-6"
            >
                <Select options={ordFormOptions} />
            </Form.Item>
            <div className="mb-6">
                <Form.Item
                    label={t('Enter creative text')}
                    name="ordText"
                    required={ordTextIsRequires}
                    className="mb-0"
                    rules={[
                        {
                            required: ordTextIsRequires,
                            whitespace: true,
                            message: t('Required field'),
                        },
                        {
                            max: 40000,
                            message: t('Maximum length of characters', { max: 40000 }),
                        },
                    ]}
                >
                    <InputText.TextArea theme="bordered" placeholder={t('Example, my creative')} />
                </Form.Item>
                <span className="text-p3 text-blueGray-500">
                    {t(
                        'The field is required for creative types: text block and text-graphic block. Up to 40,000 characters available',
                    )}
                </span>
            </div>
            {ordRimIsRequired && (
                <>
                    <Form.Item name="ordRim" label={t('Choose file')} required={ordRimIsRequired}>
                        <BaseDragAndDrop
                            allowedTypes="image/jpg, image/jpeg, image/png, image/heic, image/heif"
                            maxFilesSizeMb={10}
                            error={fileError}
                            onChange={onAddFile}
                            isLoading={isLoading}
                            multiple={false}
                        />
                    </Form.Item>
                    <Form.Item
                        label={t('Creative image description')}
                        name="ordRimDescription"
                        required={ordRimIsRequired}
                        rules={[
                            {
                                required: true,
                                whitespace: true,
                                message: t('Required field'),
                            },
                            {
                                max: 1000,
                                message: t('Maximum length of characters', { max: 1000 }),
                            },
                            {
                                pattern: WITHOUT_SPACE_START_END,
                                message: t(
                                    'There should be no space or line breaks at the beginning or end',
                                ),
                            },
                        ]}
                    >
                        <InputText.TextArea
                            theme="bordered"
                            placeholder={t('For example: Photo of a cat')}
                        />
                    </Form.Item>
                </>
            )}
            <Form.Item
                label={t('Add a description in the format Brand name. Type of product or service')}
                name="ordDescription"
                required
                rules={[
                    {
                        required: true,
                        whitespace: true,
                        message: t('Required field'),
                    },
                    {
                        max: 1000,
                        message: t('Maximum length of characters', { max: 1000 }),
                    },
                ]}
            >
                <InputText placeholder={t('Example: Brand Puma. Running shoes')} />
            </Form.Item>

            <div className="flex flex-col xs:items-center xs:flex-row xs:justify-between">
                {reductionListLoading ? (
                    <Skeleton.Input active className="!w-full" />
                ) : (
                    <div>
                        <Checkbox onChange={onCheckReductionsType}>
                            {t('Shorten link via')}:
                        </Checkbox>
                        <Select
                            id="reducers-list"
                            options={reductionList}
                            value={reductionType}
                            disabled={!reductionsNeed}
                            onChange={onChangeReductionType}
                        />
                    </div>
                )}
                <Button type="primary" submit className="mt-4" disabled={!form.isFieldsTouched()}>
                    {t('Send')}
                </Button>
            </div>
        </Form>
    );
};

export default FormMarking;
